import "./searchResults.scss";

import { Card } from "@amzn/stencil-react-components/card";
import { useResizeObserver } from "@amzn/stencil-react-components/hooks";
import { Col, Hr, Row, View } from "@amzn/stencil-react-components/layout";
import { Link, LinkTextDecorationOptions } from "@amzn/stencil-react-components/link";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import {
  CFMModuleName,
  CustomerFeedback,
  CustomerFeedbackAlignment,
  CustomerFeedbackIconPosition,
  CustomerFeedbackMargins,
} from "common/components";
import { NoResultsFound } from "common/components/NoResultsFound";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { FacetsHelper } from "common/helpers/facets-helper";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { UserPermissions } from "lib/services";
import CatalogItemsList from "modules/components/CatalogItemsList";
import { useSearchCatalogItems } from "modules/components/CatalogItemsList/hook";
import FacetedFilter from "modules/components/FacetedFilter";
import React, { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import SearchResultsV2 from "../SearchResultsV2";

const CATALOG_ITEMS_LIST_CONTAINER_ID = "search-results-column";
const WILL_UPDATE_FACETS = true;

const SearchResults: FC = () => {
  const toggleSearchExperience = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_SEARCH_EXPERIENCE);

  const [cfmStyle, setCfmStyle] = useState("cfm-scroll");

  /**
   * @description Maintains a "primary" copy of all available tags from the initial fetch.
   * This allows us to keep showing all tags in the sidebar even after a user selects one.
   * Without this approach, selecting a tag would cause the tag list to be filtered down
   * to just the selected tag due to the backend's facet updating behavior.
   */
  const [allTagsFacets, setAllTagsFacets] = useState<any[] | undefined>(undefined);

  /**
   * @description Tracks whether we've successfully captured the initial set of tags.
   * Used to determine when we should store the complete tag list.
   */
  const [initialTagsFetched, setInitialTagsFetched] = useState(false);

  /**
   * @description Tracks whether the initial data fetch has completed.
   * Used to prevent unnecessary refetching during component initialization.
   */
  const initialFetchDone = useRef(false);

  /**
   * @description Used for cleanup to prevent state updates after component unmount.
   */
  const isMounted = useRef(true);

  /**
   * @description Ref to detect the first render so that the
   * query-change effect can skip running on mount. This allows
   * the initial URL-based category filter to be applied first.
   */
  const didMount = useRef(false);

  const location = useLocation();
  const { ref } = useResizeObserver<HTMLDivElement>();

  /**
   * @description Cleanup effect to prevent state updates after component unmount.
   * Sets isMounted ref to false when component unmounts.
   */
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * @description Extracts search query and category filter from URL parameters.
   * This enables direct linking to filtered search results and maintains
   * filter state in the URL for bookmarking and sharing.
   */
  let queryValue: string | null = "";
  let categoryValue: string | null = "";
  const params = new URLSearchParams(window.location.search);
  const hash = window.location.hash.replace("#", "");

  if (location?.search) {
    queryValue = params.get("query");
    categoryValue = params.get("category"); // The tag filter value from URL
  }

  const {
    items,
    isLoading,
    isLoadingNextItems,
    searchCatalogItems,
    searchNext,
    totalCount,
    facets,
    searchParameters,
    nextToken,
  } = useSearchCatalogItems();

  const tagsToExclude = ["Top-picks", "Communication-tools", "Popular"];

  /**
   * @description Handles search query changes after initial page load.
   *
   * When the user changes the search query:
   * 1. Clears the stored primary tag list to prepare for fresh data
   * 2. Removes any existing category filter from the URL
   * 3. Resets the initialization flag to trigger a complete refresh
   * 4. Performs a new search with the updated query and no tag filters
   *
   * This ensures that changing the search query gives fresh, unfiltered results
   * and updates both the displayed items and available filters appropriately.
   */
  useEffect(() => {
    // First render: mark as mounted and do nothing
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    // Subsequent renders: always trigger a fresh search
    setAllTagsFacets(undefined);
    setInitialTagsFetched(false);

    // Remove "category" from the URL
    clearTagsFilter();

    // Reset the "initialFetchDone" so the next fetch will re-capture everything
    initialFetchDone.current = false;

    const searchText = queryValue ?? "";
    const sortParam = queryValue ? null : "name ASC";
    const paramsForSearch = {
      text: searchText,
      pageSize: 20,
      sort: sortParam,
      // unfiltered tags
    };

    // Re-run a fresh unfiltered fetch
    searchCatalogItems(paramsForSearch, WILL_UPDATE_FACETS, false);
    // We deliberately do NOT list searchCatalogItems or clearTagsFilter
    // as dependencies, because we only want to re-run this effect when queryValue truly changes.
  }, [queryValue]);

  /**
   * @description Performs the initial data fetch when the component first mounts.
   *
   * This effect runs once on component initialization to:
   * 1. Load the initial set of search results based on URL parameters
   * 2. Retrieve the complete set of available tags and categories (facets)
   * 3. Allow the facets‐handler effect to detect and apply any `?category=…` filter
   *
   * The initial fetch provides the baseline data needed for both search results
   * and the complete tag list that will be preserved even after filtering.
   */
  useEffect(() => {
    if (!initialFetchDone.current && isMounted.current) {
      const searchText = queryValue ?? "";
      const sortParam = queryValue ? null : "name ASC";
      const initialParams = {
        text: searchText !== "*" ? searchText : "",
        pageSize: 20,
        sort: sortParam,
      };

      // 1st unfiltered fetch (willUpdateFacets = true so we get both tags & categories)
      searchCatalogItems(initialParams, WILL_UPDATE_FACETS, true);
      initialFetchDone.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @description Processes facet data when it changes and handles URL-based filtering.
   *
   * This effect has two key responsibilities:
   * 1. Capture and store the complete list of tags from the initial fetch
   * This "primary list" allows us to keep showing all tags even after filtering
   *
   * 2. Handle direct URL navigation with a category filter parameter
   * If a user lands directly on a URL with ?category=tag-x, we need to:
   * - First capture the complete tag list from an unfiltered fetch
   * - Then apply the requested tag filter while preserving the complete tag list
   *
   * This approach ensures consistent behavior whether a user navigates through
   * the UI or lands directly on a filtered URL.
   */
  useEffect(() => {
    if (!facets) {
      return;
    }

    const allTags = FacetsHelper.getValuesFromFacets(facets, "tags");
    const filteredTags = FacetsHelper.excludeFacetsFromList(allTags, tagsToExclude);

    // If we have not yet saved the complete "primary" tag list, do so now
    if (!initialTagsFetched && filteredTags.length > 0) {
      setAllTagsFacets(filteredTags);
      setInitialTagsFetched(true);

      // If someone landed directly with "?category=tag-x", apply that filter now
      if (categoryValue && searchParameters && (!searchParameters.tags || searchParameters.tags.length === 0)) {
        const searchText = queryValue ?? "";
        const sortParam = queryValue ? null : "name ASC";
        const filteredParams = {
          text: searchText,
          pageSize: 20,
          sort: sortParam,
          tags: [categoryValue],
        };

        // We still do "willUpdateFacets = true" so that categories re-compute under this tag,
        // but since initialTagsFetched is now true, we do NOT overwrite our saved "allTagsFacets."
        searchCatalogItems(filteredParams, WILL_UPDATE_FACETS, false);
      }
    }

    // Otherwise, do nothing to tags here—categories will be drawn from `facets.categories` below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facets]);

  /**
   * @description Handles tag filter reset when the category parameter is cleared.
   *
   * When the user clicks "Browse all" or otherwise clears the category filter:
   * 1. We reset our stored tag list to force a fresh fetch of all available tags
   * 2. The FacetedFilter component will trigger a new search without the tag filter
   *
   * This ensures that when returning to an unfiltered state, we get fresh data
   * rather than potentially stale cached tag information.
   */
  useEffect(() => {
    if (!categoryValue) {
      setAllTagsFacets(undefined);
      setInitialTagsFetched(false);
      // The FacetedFilter's onClick -> searchCatalogItems will fire next.
    }
  }, [categoryValue]);

  //
  // onScroll “load more”:
  //
  const onLoadMore = () => {
    if (nextToken && !isLoadingNextItems && !isLoading && isMounted.current) {
      searchNext({ ...searchParameters, pageSize: 20, nextToken: nextToken, includeFacets: false });
    }
  };

  const shouldLoadMore = () => {
    if (!ref.current) {
      return false;
    }
    return !hasVisibleScroll();
  };

  // Compute “category facets” fresh from the *current* facets object:
  let categoryFacets = FacetsHelper.getValuesFromFacets(facets, "categories");
  categoryFacets = FacetsHelper.excludeFacetsFromList(categoryFacets, ["Other"]);

  /**
   * @description Updates the URL with the selected tag filter without page reload.
   *
   * This function:
   * 1. Adds the selected tag as a "category" parameter in the URL
   * 2. Updates browser history using replaceState to avoid navigation
   * 3. Returns updated search parameters with the selected tag filter
   *
   * @param {string} filterValue - The tag value to set as the filter
   * @returns {Object} Updated search parameters with the tag filter applied
   */
  const setTagsFilter = (filterValue: string) => {
    params.set("category", filterValue);
    window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}${hash ? "#" : ""}${hash}`);
    return { ...searchParameters, tags: [filterValue], nextToken: undefined };
  };

  /**
   * @description Removes the tag filter from the URL without page reload.
   *
   * This function:
   * 1. Removes the "category" parameter from the URL
   * 2. Updates browser history using replaceState to avoid navigation
   * 3. Returns updated search parameters with no tag filters
   *
   * @returns {Object} Updated search parameters with tag filters cleared
   */
  const clearTagsFilter = () => {
    params.delete("category");
    window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}${hash ? "#" : ""}${hash}`);
    return { ...searchParameters, tags: [], nextToken: undefined };
  };

  const hasVisibleScroll = () => {
    if (ref?.current) {
      return ref.current.clientHeight < ref.current.scrollHeight;
    }
    return false;
  };

  useLayoutEffect(() => {
    if (isMounted.current) {
      hasVisibleScroll() ? setCfmStyle("cfm-scroll") : setCfmStyle("cfm-no-scroll");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hasVisibleScroll(), ref]);

  // WILL RETURN THE NEW SEARCH EXPERIENCE
  if (toggleSearchExperience) {
    return <SearchResultsV2 />;
  }

  if (totalCount === 0) {
    if (isLoading) {
      return (
        <View padding="S700">
          <Spinner size={SpinnerSize.Medium} fr={undefined} />
        </View>
      );
    }

    return (
      <PageMainContainer>
        <Row padding={{ top: "S300" }} gridGap="S400" width={"100%"}>
          <Text fontSize="T300" className="search-results-number">
            {getString("noResults.searchResultText", { text: queryValue })}
          </Text>
        </Row>
        <NoResultsFound searchTerm={queryValue} />
      </PageMainContainer>
    );
  }

  return (
    <View className="search-results-container">
      <Row width="100%" height="100%">
        <Col width={297} height="100%" backgroundColor="neutral05">
          <Col width={297} className="search-results-column" height="100%">
            {/* 
              Tag Filter Component
              - Uses the preserved "primary" tag list (allTagsFacets) instead of current facets
              - This ensures all tags remain visible even after selecting one
              - Integrates with URL synchronization via setTagsFilter/clearTagsFilter
              - Automatically selects the tag from URL if present (categoryValue)
            */}
            <FacetedFilter
              facets={allTagsFacets}
              title={getString("categoryFilter.categoryTitle")}
              allOptionText={getString("categoryFilter.allCategories")}
              setSearchParameters={setTagsFilter}
              clearSearchParameters={clearTagsFilter}
              defaultSelected={categoryValue ?? ""}
            />

            {/* 
              Categories (OS) Filter Component
              - Uses the current facets.categories which updates based on selected tag
              - When a tag is selected, this list dynamically updates to show only
                categories that exist within that tag's items
              - Unlike tags, we don't preserve the original list of categories
            */}
            <FacetedFilter
              facets={categoryFacets}
              title={getString("categoryFilter.osTitle")}
              allOptionText={getString("categoryFilter.allOperatingSystems")}
              setSearchParameters={(filterValue: string) => {
                return { ...searchParameters, categories: [filterValue], nextToken: undefined };
              }}
              clearSearchParameters={() => {
                return { ...searchParameters, categories: [], nextToken: undefined };
              }}
            />
          </Col>

          <Col width={297} padding={[0, 0, "S400", 0]} height="110px" justifyContent="flex-end">
            <Col width="70%" margin={["S500", 0, 0, "S500"]}>
              <Hr size="wide" color="neutral30" />
            </Col>

            <Text color="neutral70" margin={["S300", 0, 0, "S500"]} fontSize="T14">
              {getString("submitSoftwareRequest.header")}
            </Text>
            <Link
              textDecoration={LinkTextDecorationOptions.None}
              fontWeight="bold"
              href={getString("externalLinks.uniqueRequests")}
              color="primary80"
              margin={["S300", 0, 0, 0]}
              padding={[0, 0, 0, "S500"]}
              fontSize="T14"
              data-cy="submit-request-link"
            >
              {getString("submitSoftwareRequest.link")}
            </Link>
          </Col>
        </Col>

        <Col
          className="search-results-column"
          id={CATALOG_ITEMS_LIST_CONTAINER_ID}
          ref={ref}
          padding={"S500"}
          gridGap={"S250"}
        >
          {isLoading ? (
            <View padding="S700">
              <Spinner size={SpinnerSize.Medium} fr={undefined} />
            </View>
          ) : (
            <>
              <Text fontSize="T300" color="neutral90">
                {queryValue?.trim()
                  ? getString("catalogItemsList.resultsMessage", { totalCount: totalCount, text: queryValue })
                  : getString("catalogItemsList.browseAllMessage", { totalCount: totalCount })}
              </Text>
              <CatalogItemsList
                onSearchNext={onLoadMore}
                scrollableTarget={CATALOG_ITEMS_LIST_CONTAINER_ID}
                shouldLoadMore={shouldLoadMore}
                items={items}
                isLoading={isLoading}
                isLoadingNextItems={isLoadingNextItems}
                totalCount={totalCount}
              />
            </>
          )}
        </Col>
      </Row>
      <Card
        className={`cfm-general ${cfmStyle}`}
        data-testid="cfm-card"
        isElevated={true}
        border={"1px solid neutral50"}
        style={{ position: "absolute", top: "55%" }}
      >
        <CustomerFeedback
          moduleName={CFMModuleName.searchResults}
          alignment={CustomerFeedbackAlignment.Centered}
          position={CustomerFeedbackIconPosition.Bottom}
          margin={CustomerFeedbackMargins.None}
        />
      </Card>
    </View>
  );
};

export default SearchResults;
